<script setup>
import { Link, router } from '@inertiajs/vue3'
import TextInput from '@/Shared/TextInput.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import { useForm } from '@inertiajs/vue3'
import { useConfirm } from '@/Composables/useConfirm'
import { useDate } from '@/Composables/useDate'
import { useInitials } from '@/Composables/useInitials'
import Pagination from '@/Shared/Pagination.vue'
import { ref, computed } from 'vue'
import MultiselectInput from '@/Shared/MultiselectInput.vue'
import Portrait from '@/Shared/Portrait.vue'
import Icon from '@/Shared/Icon.vue'
import SearchInput from '@/Shared/SearchInput.vue'
import StatusFilterInput from '@/Shared/StatusFilterInput.vue'

const props = defineProps({
  ranking: {
    type: Object,
    required: true
  },
  filters: {
    type: Object,
    default: () => {}
  },
  teams: {
    type: Object,
    default: () => {}
  },
  modalities: {
    type: Array,
    required: true
  },
  statuses: {
    type: Array,
    required: true
  },
  players: {
    type: Array,
    required: true
  }
})

const search = ref(props.filters.search)
const phaseFilter = ref('all')

// Añadir opción para ver todas las fases
const allStatuses = computed(() => {
  return [{ name: 'Todas', color: 'gray', value: 'all' }, ...props.statuses]
})

let form = useForm({
  name: props.ranking.name,
  modality: props.ranking.modality,
  admins: props.ranking.admins
})

function update() {
  form.put(`/rankings/${props.ranking.id}/update`)
}

function phaseStatus(status) {
  return props.statuses.find((s) => s.value === status)
}

const filteredPhases = computed(() => {
  if (phaseFilter.value === 'all') {
    return props.ranking.phases
  }

  return props.ranking.phases.filter((phase) => phase.status === phaseFilter.value)
})

async function deleteRanking() {
  if (await useConfirm('¿Estas seguro de que quieres eliminar el ranking?')) {
    router.delete(`/rankings/${props.ranking.id}/delete`)
  }
}

const breadcrumbs = [{ label: 'Rankings', href: '/rankings' }, { label: form.name }]

const { getInitials } = useInitials()
</script>

<template>
  <div class="space-y-8">
    <!-- Portrait 1: Información del Ranking -->
    <Portrait
      title="Editar ranking"
      subtitle="Gestiona la información general de este ranking"
      :breadcrumbs="breadcrumbs"
      :footer-actions="true"
    >
      <form class="bg-white rounded-lg" @submit.prevent="update">
        <div class="flex flex-wrap gap-6 p-1">
          <TextInput
            v-model="form.name"
            :error="form.errors.name"
            class="w-full lg:w-1/2"
            label="Nombre"
          />
          <SelectInput
            :model-value="form.modality"
            class="w-full lg:w-1/2"
            label="Modalidad"
            disabled
          >
            <option v-for="modality in modalities" :key="modality.value" :value="modality.value">
              {{ modality.name }}
            </option>
          </SelectInput>
          <MultiselectInput
            v-model="form.admins"
            :options="players"
            :error="form.errors.admins"
            class="w-full"
            label="Administradores"
            input-label="fullname"
            track-by="fullname"
          />
        </div>
      </form>

      <template #footer>
        <div class="flex items-stretch justify-end w-full space-x-4">
          <button class="btn-red" tabindex="-1" type="button" @click="deleteRanking">
            Eliminar
          </button>

          <LoadingButton :loading="form.processing" class="btn-indigo" @click="update">
            Guardar
          </LoadingButton>
        </div>
      </template>
    </Portrait>

    <!-- Portrait 2: Fases -->
    <Portrait title="Fases" subtitle="Gestiona las fases de este ranking">
      <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <StatusFilterInput
          v-model="phaseFilter"
          :options="allStatuses"
          label="Filtrar por estado"
          class="w-64"
          :status-colors="
            Object.fromEntries(
              [...allStatuses].map((status) => [status.value, status.color || 'bg-gray-400'])
            )
          "
        />
        <Link
          class="btn-indigo flex items-center justify-center"
          :href="`/rankings/${ranking.id}/phases/create`"
        >
          <Icon name="Plus" class="h-5 w-5 mr-2" />
          <span>Nueva fase</span>
        </Link>
      </div>

      <!-- Lista de fases -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <thead>
              <tr class="text-left font-medium text-gray-700 bg-gray-50 border-b border-gray-200">
                <th class="px-6 py-3.5">Nombre</th>
                <th class="px-6 py-3.5">Estado</th>
                <th class="px-6 py-3.5">Fecha inicio</th>
                <th class="px-6 py-3.5">Fecha fin</th>
                <th class="px-6 py-3.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="phase in filteredPhases"
                :key="phase.id"
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                @click="$inertia.visit(`/rankings/phases/${phase.id}/edit`)"
              >
                <td class="px-6 py-4">
                  <div class="font-medium text-gray-900">{{ phase.name }}</div>
                </td>
                <td class="px-6 py-4">
                  <span
                    class="px-2.5 py-1 inline-flex text-xs leading-4 font-medium rounded-full"
                    :style="{ backgroundColor: phaseStatus(phase.status).color }"
                  >
                    {{ phaseStatus(phase.status).name }}
                  </span>
                </td>
                <td class="px-6 py-4 text-gray-700">
                  {{ useDate(phase.date_start).format('DD-MM-YYYY') }}
                </td>
                <td class="px-6 py-4 text-gray-700">
                  {{ useDate(phase.date_end).format('DD-MM-YYYY') }}
                </td>
                <td class="px-6 py-4 text-right">
                  <span class="text-indigo-600 font-medium text-sm inline-flex items-center">
                    <span>Editar</span>
                    <Icon name="Pencil" class="h-5 w-5 ml-1" />
                  </span>
                </td>
              </tr>

              <!-- Estado vacío -->
              <tr v-if="filteredPhases.length === 0">
                <td class="px-6 py-10 text-center text-gray-500 border-b" colspan="5">
                  <div class="flex flex-col items-center justify-center">
                    <Icon name="Warning" class="h-10 w-10 text-gray-400 mb-2" />
                    <p class="text-lg font-medium">No hay fases todavía</p>
                    <p class="text-sm mt-1">No hay fases en el estado seleccionado</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Portrait>

    <!-- Portrait 3: Equipos (solo si aplica) -->
    <Portrait
      v-if="ranking.modality === 'doubles'"
      title="Equipos"
      subtitle="Gestiona los equipos de este ranking"
    >
      <!-- Barra de herramientas superior -->
      <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <SearchInput
          v-model="search"
          class="w-full sm:w-64"
          placeholder="Buscar equipos..."
          :url="`/rankings/${ranking.id}/edit`"
          :debounce-time="250"
          :only="['teams']"
        />

        <Link
          class="btn-indigo flex items-center justify-center"
          :href="`/rankings/${ranking.id}/teams/create`"
        >
          <Icon name="Plus" class="h-5 w-5 mr-2" />
          <span>Nuevo equipo</span>
        </Link>
      </div>

      <!-- Lista de equipos -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <thead>
              <tr class="text-left font-medium text-gray-700 bg-gray-50 border-b border-gray-200">
                <th class="px-6 py-3.5">Nombre</th>
                <th class="px-6 py-3.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="team in teams.data"
                :key="team.id"
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                @click="$inertia.visit(`/rankings/teams/${team.id}/edit`)"
              >
                <td class="px-6 py-4">
                  <div class="flex items-center">
                    <div
                      class="bg-indigo-100 text-indigo-700 rounded-full h-10 w-10 flex items-center justify-center font-bold mr-3"
                    >
                      {{ getInitials(team.name, '/') }}
                    </div>
                    <div class="font-medium text-gray-900">{{ team.name }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 text-right">
                  <span class="text-indigo-600 font-medium text-sm inline-flex items-center">
                    <span>Editar</span>
                    <Icon name="Pencil" class="h-5 w-5 ml-1" />
                  </span>
                </td>
              </tr>

              <!-- Estado vacío -->
              <tr v-if="teams.data.length === 0">
                <td class="px-6 py-10 text-center text-gray-500 border-b" colspan="2">
                  <div class="flex flex-col items-center justify-center">
                    <Icon name="Warning" class="h-10 w-10 text-gray-400 mb-2" />
                    <p class="text-lg font-medium">No hay equipos todavía</p>
                    <p class="text-sm mt-1">Crea un nuevo equipo para comenzar</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Pagination class="mt-6" :links="teams.links" />
    </Portrait>
  </div>
</template>
